<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import FavoriteFiltersList from './FavoriteFiltersList.vue';
    import { __ } from '@/js/stores/expressions';
    import AddFavoriteFilterModal from './AddFavoriteFilterModal.vue';
    import { useFilterStoreByName } from '@/js/stores/filter';
    import { FilterStoreName } from '@/js/services/api/filter/types';
    import EchoButton from '../EchoButton.vue';
    import {
        useDeleteFavoriteFilter,
        useGetFavoriteFilter,
        useGetFavoriteFilters,
    } from '@/js/hooks/favoriteFilter';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import EchoPopover from '../EchoPopover.vue';
    import { ChevronDownIcon, ChevronUpIcon, StarIcon } from '@lucide/vue';

    const props = defineProps<{
        filterStoreName: FilterStoreName;
    }>();

    const showAddModal = defineModel<boolean>('showAddModal', { default: false });
    const filterStore = useFilterStoreByName(props.filterStoreName);
    const { data: favoriteFiltersData } = useGetFavoriteFilters();

    watch(
        () => filterStore.favoriteComparisonKey,
        (comparisonKey) => {
            if (
                filterStore.selectedFavoriteFilter &&
                filterStore.selectedFavoriteFilterRequestData
            ) {
                if (comparisonKey !== filterStore.selectedFavoriteFilterRequestData) {
                    filterStore.clearSelectedFavoriteFilter();
                }
            }
        }
    );
    const confirm = useConfirmationDialog();

    const isLoadingFilter = ref(false);

    const { mutateAsync: deleteFavorite } = useDeleteFavoriteFilter();
    const { mutateAsync: loadFavorite } = useGetFavoriteFilter();

    const onSelectFilter = async (filter: { id: string; name: string }) => {
        if (isLoadingFilter.value) return;
        isLoadingFilter.value = true;

        try {
            const fav = await loadFavorite(filter.id);
            await filterStore.applyFilterRequest(fav.favoriteFilter);
            filterStore.setSelectedFavoriteFilter({ id: filter.id, name: filter.name });
        } finally {
            isLoadingFilter.value = false;
            popoverRef.value?.close();
        }
    };

    const onDeleteFilter = async (filter: { id: string; name: string }) => {
        popoverRef.value?.close();

        const ok = await confirm.openDialog({
            message: __('favorite-filters-delete-confirmation', {
                filterName: filter.name,
            }),
        });

        if (!ok) return;

        await deleteFavorite(filter.id);
        filterStore.clearSelectedFavoriteFilter();
    };

    const popoverRef = ref<InstanceType<typeof EchoPopover>>();

    const isLoading = computed(() => {
        if (isLoadingFilter.value) {
            return true;
        }

        return filterStore.loading && !!filterStore.selectedFavoriteFilter;
    });
</script>
<template>
    <div class="flex items-center gap-2">
        <div class="relative">
            <EchoPopover
                v-if="(favoriteFiltersData ?? []).length > 0"
                ref="popoverRef"
                type="menu"
                position="bottom-end"
            >
                <template #button="{ anchor, togglePopover, open, buttonAttrs }">
                    <EchoButton
                        :ref="anchor"
                        variant="secondary"
                        size="small"
                        :loading="isLoading"
                        :title="__('show-hide-column-modal-chosen-filters')"
                        v-bind="buttonAttrs"
                        @click="togglePopover"
                    >
                        <template #icon-left><StarIcon /></template>
                        <span class="max-w-48 truncate">
                            {{ filterStore.selectedFavoriteFilter?.name ?? __('favorite-filters') }}
                        </span>
                        <template #icon-right>
                            <ChevronUpIcon v-if="open" />
                            <ChevronDownIcon v-else />
                        </template>
                    </EchoButton>
                </template>

                <template #popover>
                    <FavoriteFiltersList
                        :list="favoriteFiltersData ?? []"
                        @select="onSelectFilter"
                        @delete="onDeleteFilter"
                    />
                </template>
            </EchoPopover>
        </div>
    </div>
    <AddFavoriteFilterModal
        :show="showAddModal"
        :filter-store-name="filterStoreName"
        @close="showAddModal = false"
    />
</template>
